import { useEffect, useMemo, useState } from "react";
import { soulManager } from "../../data/managers/SoulManager";
import { SoulDataModel } from "../../data/models/Soul/SoulDataModel";

const normalizeText = (value: unknown) => String(value ?? "").toLowerCase();

const getCreatedAtTimestamp = (user: SoulDataModel) => {
  if (!user.createdAt) return 0;

  const timestamp = Date.parse(user.createdAt);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

export function useAdminUsers() {
  const [users, setUsers] = useState<SoulDataModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState("");

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await soulManager.getAllSouls();
      setUsers(res ?? []);
    } catch (e) {
      console.error("Error cargando usuarios:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchUsers();
  }, []);

  const filtered = useMemo(() => {
    const lower = search.trim().toLowerCase();

    if (!lower) {
      return users;
    }

    return users.filter((u) => {
      const fields = [u.fullName, u.email, u.passport, u.role, u.plan?.name];
      return fields.some((field) => normalizeText(field).includes(lower));
    });
  }, [users, search]);

  const orderedUsers = useMemo(() => {
    return [...filtered].sort(
      (a, b) => getCreatedAtTimestamp(b) - getCreatedAtTimestamp(a),
    );
  }, [filtered]);

  const handleExportCSV = () => {
    const escapeCsv = (value: unknown) =>
      `"${String(value ?? "").replace(/"/g, '""')}"`;

    const csv = [
      ["ID", "Nombre", "Correo", "Rol", "Plan", "Pasaporte", "Creado"],
      ...users.map((u) => [
        u.userId ?? "",
        u.fullName ?? "",
        u.email ?? "",
        u.role ?? "",
        u.plan?.name ?? "",
        u.passport ?? "",
        u.createdAt ?? "",
      ]),
    ]
      .map((row) => row.map(escapeCsv).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "usuarios.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const deleteAdminUser = async (
    user: SoulDataModel,
    confirmDeletion: boolean,
  ) => {
    setDeleting(true);
    try {
      await soulManager.deleteAdminUser({
        recordType: user.recordType || "soul",
        userId: user.userId,
        previewId: user.previewId,
        confirmDeletion,
      });
      await fetchUsers();
    } finally {
      setDeleting(false);
    }
  };

  return {
    users: orderedUsers,
    loading,
    deleting,
    search,
    setSearch,
    handleExportCSV,
    deleteAdminUser,
    fetchUsers,
  };
}
